import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, AlertCircle } from 'lucide-react';
import { WorkerProject, WorkerDocument } from '../types/worker.types';
import { workerService } from '../services/worker.service';
import { WorkerLayout } from '../components/layout/WorkerLayout';
import { DocumentCard } from '../components/modules/documents/DocumentCard';
import { DocumentSearch } from '../components/modules/documents/DocumentSearch';
import { LoadingPage, Card, CardContent } from '../components/ui';

interface WorkerDocumentsProps {
  project: WorkerProject;
  token: string;
}

export function WorkerDocuments({ project, token }: WorkerDocumentsProps) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<WorkerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleBack = () => {
    navigate(`/worker/${token}`);
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await workerService.getProjectDocuments(project.id);
      
      if (response.error) {
        setError(response.error.message);
      } else {
        setDocuments(response.data || []);
      }
    } catch (err) {
      setError('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [project.id]);

  const filteredDocuments = useMemo(() => {
    let filtered = documents;

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(doc =>
        doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtrer par catégorie
    if (selectedCategory) {
      filtered = filtered.filter(doc => doc.category === selectedCategory);
    }

    return filtered;
  }, [documents, searchTerm, selectedCategory]);

  const availableCategories = useMemo(() => {
    const categories = new Set<string>();
    documents.forEach(doc => {
      if (doc.category) {
        categories.add(doc.category);
      }
    });
    return Array.from(categories).sort();
  }, [documents]);

  const documentsByCategory = useMemo(() => {
    const grouped: Record<string, WorkerDocument[]> = {};
    
    filteredDocuments.forEach(doc => {
      const category = doc.category || 'Autres';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(doc);
    });

    return grouped;
  }, [filteredDocuments]);

  if (loading) {
    return (
      <WorkerLayout 
        project={project} 
        onBack={handleBack}
        showBackButton
        title="Documents"
      >
        <LoadingPage message="Chargement des documents..." />
      </WorkerLayout>
    );
  }

  if (error) {
    return (
      <WorkerLayout 
        project={project} 
        onBack={handleBack}
        showBackButton
        title="Documents"
      >
        <Card className="bg-red-50 border-red-200">
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout 
      project={project} 
      onBack={handleBack}
      showBackButton
      title="Documents"
    >
      <div className="space-y-6">
        {/* Recherche et filtres */}
        <DocumentSearch
          searchTerm={searchTerm}
          selectedCategory={selectedCategory}
          categories={availableCategories}
          onSearchChange={setSearchTerm}
          onCategoryChange={setSelectedCategory}
        />

        {/* Résultats */}
        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm || selectedCategory 
                  ? 'Aucun document trouvé pour ces critères'
                  : 'Aucun document disponible pour ce projet'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Statistiques */}
            <Card>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      {filteredDocuments.length} document{filteredDocuments.length > 1 ? 's' : ''}
                      {searchTerm || selectedCategory ? ' trouvé' : ' disponible'}{filteredDocuments.length > 1 ? 's' : ''}
                    </p>
                    {availableCategories.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {availableCategories.length} catégorie{availableCategories.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>

            {/* Documents groupés par catégorie */}
            {Object.entries(documentsByCategory).map(([category, categoryDocuments]) => (
              <div key={category} className="space-y-3">
                <h3 className="font-medium text-gray-900 flex items-center">
                  {category}
                  <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    {categoryDocuments.length}
                  </span>
                </h3>
                <div className="space-y-3">
                  {categoryDocuments.map((document) => (
                    <DocumentCard
                      key={document.id}
                      document={document}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
}